import React from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { logger } from "@/lib/logger";

export default function Locations() {
  const { projectId } = useParams();
  const [locs, setLocs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState({});
  const { updateCredits } = useAuth();

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/locations`);
      setLocs(data);
    } catch (err) {
      logger.warn("locations: load failed", err);
      toast.error("Could not load");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load]);

  const gen = async (l) => {
    setBusy((s) => ({ ...s, [l.id]: true }));
    try {
      const { data } = await api.post("/storyboards/portrait", { project_id: projectId, kind: "location", entity_id: l.id });
      updateCredits(data.credits);
      toast.success("Concept art generated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy((s) => ({ ...s, [l.id]: false })); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto px-8 py-10">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Locations</div>
        <h1 className="font-editorial text-4xl mb-8">Where the film lives</h1>

        {loading ? (
          <div className="text-[#A1A1AA]">Loading…</div>
        ) : locs.length === 0 ? (
          <div className="border border-[#1E1E1E] bg-[#0F0F10] p-12 text-center">
            <div className="font-editorial text-2xl">No locations yet.</div>
            <p className="text-sm text-[#A1A1AA] mt-2">Scene headings define locations. Write a heading and this board will populate itself.</p>
            <Link to={`/app/projects/${projectId}/script`} className="mt-6 inline-block bg-[#D4AF37] text-[#0A0A0A] px-5 py-2 text-sm font-medium">Open screenplay</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {locs.map((l) => (
              <LocationCard key={l.id} l={l} projectId={projectId} onGen={() => gen(l)} busy={busy[l.id]} />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function LocationCard({ l, projectId, onGen, busy }) {
  const [dataUrl, setDataUrl] = React.useState(null);
  React.useEffect(() => {
    if (l.image_asset_id) {
      api.get(`/storyboards/asset/${l.image_asset_id}`).then((r) => setDataUrl(r.data.data_url)).catch(() => {});
    } else { setDataUrl(null); }
  }, [l.image_asset_id]);

  return (
    <div className="border border-[#1E1E1E] bg-[#141414] p-5" data-testid={`loc-card-${l.id}`}>
      <div className="aspect-video bg-[#0A0A0A] border border-[#27272A] relative overflow-hidden mb-4">
        {dataUrl ? (
          <img src={dataUrl} alt={l.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-[#71717A]">
            <ImageIcon size={22} strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-widest mt-1">No concept art</div>
          </div>
        )}
        <button onClick={onGen} disabled={busy}
          className="absolute bottom-2 right-2 bg-[#0A0A0A]/90 border border-[#27272A] hover:border-[#D4AF37] text-[10px] uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1 transition-colors"
          data-testid={`loc-gen-${l.id}`}>
          {busy ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} className="text-[#D4AF37]" />}
          {dataUrl ? "Regenerate" : "Generate"}
        </button>
      </div>
      <div className="flex items-baseline justify-between">
        <div className="font-editorial text-xl">{l.name}</div>
        <div className="text-[10px] uppercase tracking-widest text-[#71717A]">{l.type}</div>
      </div>
      <div className="mt-2 text-xs text-[#A1A1AA]">
        {l.scene_count} scene{l.scene_count === 1 ? "" : "s"} · {l.day_night} · complexity {l.complexity}/5
      </div>
    </div>
  );
}
